import { fixNumber } from '../../../utils/formatNumber';

export interface InvoiceLineTotalsInput {
    amount?: number | string | null;
    units?: number | string | null;
    totalWithoutTax?: number | string | null;
    totalWithTax?: number | string | null;
}

export interface InvoiceTotalsOptions {
    tax: number;
    irpf?: number | null;
    irc?: number | null;
    ivaExempt?: boolean;
}

export const computeManualLineTotals = (
    amount: number | string,
    units: number | string,
    tax: number,
    ivaExempt = false
) => {
    const amountNum = parseFloat(String(amount)) || 0;
    const unitsNum = parseInt(String(units), 10) || 0;

    if (!amountNum || !unitsNum) {
        return { totalWithoutTax: 0, totalWithTax: 0 };
    }

    const totalWithoutTax = amountNum * unitsNum;
    const totalWithTax = ivaExempt
        ? totalWithoutTax
        : totalWithoutTax * (1 + (tax || 0) / 100);

    return {
        totalWithoutTax: parseFloat(fixNumber(totalWithoutTax)),
        totalWithTax: parseFloat(fixNumber(totalWithTax)),
    };
};

export const calculateInvoiceTotals = (
    lines: InvoiceLineTotalsInput[],
    { tax, irpf, irc, ivaExempt = false }: InvoiceTotalsOptions
) => {
    let taxable = 0;
    let taxesFromLines = 0;
    let totalWithTaxSum = 0;

    lines.forEach((line) => {
        const withoutTax = parseFloat(String(line.totalWithoutTax || 0)) || 0;
        const withTax = parseFloat(String(line.totalWithTax || 0)) || 0;
        const amount = parseFloat(String(line.amount || 0)) || 0;
        const units = parseInt(String(line.units || 0), 10) || 0;

        if (withoutTax > 0 || withTax > 0) {
            const lineTaxable = withoutTax > 0 ? withoutTax : (ivaExempt ? withTax : withTax / (1 + (tax || 0) / 100));
            const lineWithTax = withTax > 0 ? withTax : (ivaExempt ? lineTaxable : lineTaxable * (1 + (tax || 0) / 100));

            taxable += lineTaxable;
            taxesFromLines += ivaExempt ? 0 : lineWithTax - lineTaxable;
            totalWithTaxSum += lineWithTax;
            return;
        }

        if (amount && units) {
            const computed = computeManualLineTotals(amount, units, tax, ivaExempt);
            taxable += computed.totalWithoutTax;
            taxesFromLines += ivaExempt ? 0 : computed.totalWithTax - computed.totalWithoutTax;
            totalWithTaxSum += computed.totalWithTax;
        }
    });

    const irpfAmt = irpf ? taxable * (irpf / 100) : 0;
    const ircAmt = irc ? taxable * (irc / 100) : 0;
    const totalTaxesAmount = ivaExempt
        ? 0
        : (taxesFromLines > 0 ? taxesFromLines : taxable * ((tax || 0) / 100));
    const grossTotal = totalWithTaxSum > 0 ? totalWithTaxSum : taxable + totalTaxesAmount;
    const totalPrice = grossTotal - irpfAmt - ircAmt;

    return {
        taxableAmount: parseFloat(fixNumber(taxable)),
        totalTaxesAmount: parseFloat(fixNumber(totalTaxesAmount)),
        totalPrice: parseFloat(fixNumber(totalPrice)),
    };
};
